import React, { useRef, useState } from 'react';
import './DataTableImportPopup.scss';
import Rodal from '../../../components/Rodal/Rodal';
import { Col, Input, Label, ModalBody, ModalHeader, Row } from 'reactstrap';
import { RadioGroup } from '@mui/material';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import ExpandableRadioButton from '../../../components/ExpandableRadioButton/ExpandableRadioButton';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import { DataTableColumn } from '../../../models/DataRequestHub/DataTable';
import {
    DataTableImportMode,
    DataTableImportModeOptions,
} from '../../../models/DataRequestHub/DataTableImportModeEnum';
import {
    AnswerTypeVariable,
    AnswerTypeVariableNamesList,
} from '../../../models/DataRequestHub/ProjectVariable';

const fileAnswerTypeName = AnswerTypeVariableNamesList.find(
    (answerType) => answerType.id === AnswerTypeVariable.File
)?.name;

interface DataTableImportPopupProps {
    columns: DataTableColumn[];
    isSubmitting: boolean;
    onClose(): void;
    onImport(
        importMode: DataTableImportMode,
        identifierCustomColumnId: string,
        file: File
    ): void;
}

export const DataTableImportPopup = (props: DataTableImportPopupProps) => {
    const [importMode, setImportMode] = useState<DataTableImportMode>(
        DataTableImportMode.Append
    );
    const [identifierCustomColumnId, setIdentifierCustomColumnId] =
        useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const isUpdateMode = importMode === DataTableImportMode.Update;
    const identifierColumns = props.columns.filter(
        (column) => column.answerType !== fileAnswerTypeName
    );
    const isConfirmDisabled =
        props.isSubmitting || (isUpdateMode && !identifierCustomColumnId);

    const handleImportModeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setImportMode(event.target.value as DataTableImportMode);
    };

    const handleConfirmClick = () => {
        if (isConfirmDisabled) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            props.onImport(
                importMode,
                isUpdateMode ? identifierCustomColumnId : null,
                file
            );
        }
        event.target.value = '';
    };

    return (
        <div className="data-table-import-popup">
            <Rodal
                visible={true}
                onClose={() => {
                    if (!props.isSubmitting) props.onClose();
                }}
                width={450}
                animation={'fade'}
                showMask={true}
                centered>
                <ModalHeader>Import Data Table</ModalHeader>
                <ModalBody>
                    <Col className="sections-container">
                        <Row className="import-mode-section">
                            <RadioGroup
                                value={importMode}
                                onChange={handleImportModeChange}>
                                {DataTableImportModeOptions.map((option) => (
                                    <div
                                        className="import-mode-option"
                                        key={option.id}>
                                        <ExpandableRadioButton
                                            value={option.id}
                                            label={option.name}
                                            isExpanded={false}
                                            expandableContent={''}
                                            disabled={props.isSubmitting}
                                        />
                                        <InfoTooltip
                                            text={option.description}
                                            place="right"
                                        />
                                    </div>
                                ))}
                            </RadioGroup>
                        </Row>
                        {isUpdateMode && (
                            <Row className="identifier-column-section">
                                <Label for="identifierCustomColumnId">
                                    Identifier Column
                                </Label>
                                <Input
                                    type="select"
                                    name="identifierCustomColumnId"
                                    value={identifierCustomColumnId}
                                    disabled={props.isSubmitting}
                                    onChange={(event) =>
                                        setIdentifierCustomColumnId(
                                            event.target.value
                                        )
                                    }>
                                    <option value="">Select column</option>
                                    {identifierColumns.map((column) => (
                                        <option
                                            key={column.customColumnId}
                                            value={column.customColumnId}>
                                            {column.name}
                                        </option>
                                    ))}
                                </Input>
                            </Row>
                        )}
                    </Col>
                    <div className="buttons-wrapper">
                        <button
                            className="btn btn-outline-secondary"
                            disabled={props.isSubmitting}
                            onClick={props.onClose}>
                            Cancel
                        </button>
                        <ButtonLoader
                            buttonText="Import"
                            loaderButtonText="Import"
                            isLoading={props.isSubmitting}
                            disabled={isConfirmDisabled}
                            onClick={handleConfirmClick}
                            className="btn btn-primary"
                        />
                    </div>
                    <input
                        hidden
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                </ModalBody>
            </Rodal>
        </div>
    );
};
